import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { WhosInside } from '@/components/dashboard/WhosInside';
import { QuickActions } from '@/components/dashboard/QuickActions';
import { StatsCards } from '@/components/dashboard/StatsCards';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { FloatingQR } from '@/components/dashboard/FloatingQR';
import { Last5DaysSales } from '@/components/dashboard/Last5DaysSales';
import { DashboardStatistics } from '@/components/dashboard/DashboardStatistics';
import { TodayReminders } from '@/components/dashboard/TodayReminders';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { storeCredentials } from '@/services/biometric';
import { checkAppVersion, getAppStoreUrl, VersionCheckResult } from '@/services/app-version-check';
import { UpdateRequiredModal } from '@/components/ui/update-required-modal';

const Index = () => {
  const { user, companyId, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [showSaveCredentialsDialog, setShowSaveCredentialsDialog] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [pendingPassword, setPendingPassword] = useState<string | null>(null);
  const [versionCheckResult, setVersionCheckResult] = useState<VersionCheckResult | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const { data: userProfile } = useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!user?.id && !authLoading,
  });

  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (error) {
        console.error('Error fetching company:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!companyId && !authLoading,
  });

  const username = userProfile?.full_name || userProfile?.email?.split('@')[0] || user?.email?.split('@')[0] || "Kullanıcı";
  const companyName = company?.name || "Şirket";

  // Check app version on mount after user is authenticated
  useEffect(() => {
    if (!user || authLoading) {
      return;
    }

    const checkVersion = async () => {
      console.log('[Index] Checking app version...');
      try {
        const versionResult = await checkAppVersion();
        console.log('[Index] Version check result:', versionResult);
        
        if (versionResult.needsUpdate && versionResult.requirement) {
          console.log('[Index] ⚠️ App version is outdated, showing update modal');
          setVersionCheckResult(versionResult);
          setShowUpdateModal(true);
        }
      } catch (versionError) {
        console.error('[Index] Error checking app version:', versionError);
        // Continue even if version check fails
      }
    };

    // Small delay to ensure component is mounted
    const timeoutId = setTimeout(checkVersion, 500);
    return () => clearTimeout(timeoutId);
  }, [user, authLoading]);

  // Check for pending credential dialog on mount
  useEffect(() => {
    console.log('[Index] useEffect triggered - user:', user ? 'exists' : 'null', 'authLoading:', authLoading);
    
    if (!user || authLoading) {
      console.log('[Index] ⏳ Waiting for user/auth...');
      return;
    }

    const checkPendingDialog = () => {
      console.log('════════════════════════════════════════════════════════════');
      console.log('[Index] 🔍 Checking for pending credential dialog...');
      
      const pendingFlag = sessionStorage.getItem('shop-insight-pending-credential-dialog');
      const email = sessionStorage.getItem('shop-insight-pending-email');
      const password = sessionStorage.getItem('shop-insight-pending-password');

      console.log('[Index] Flag:', pendingFlag);
      console.log('[Index] Email:', email ? 'exists' : 'null');
      console.log('[Index] Password:', password ? 'exists' : 'null');
      console.log('[Index] Platform:', Capacitor.getPlatform());
      console.log('[Index] Is native:', Capacitor.isNativePlatform());

      if (pendingFlag === 'true' && email && password && Capacitor.isNativePlatform()) {
        console.log('[Index] ✅ Found pending credentials, showing dialog!');
        setPendingEmail(email);
        setPendingPassword(password);
        setShowSaveCredentialsDialog(true);
        // Clear the flag immediately to prevent showing again
        sessionStorage.removeItem('shop-insight-pending-credential-dialog');
        sessionStorage.removeItem('shop-insight-pending-email');
        sessionStorage.removeItem('shop-insight-pending-password');
        console.log('[Index] ✅ Dialog state set to true');
        console.log('════════════════════════════════════════════════════════════');
      } else {
        console.log('[Index] ℹ️  No pending credentials or conditions not met');
        console.log('[Index] Conditions check:');
        console.log('  - Flag === "true":', pendingFlag === 'true');
        console.log('  - Email exists:', !!email);
        console.log('  - Password exists:', !!password);
        console.log('  - Is native:', Capacitor.isNativePlatform());
        console.log('════════════════════════════════════════════════════════════');
      }
    };

    // Small delay to ensure component is mounted
    const timeoutId = setTimeout(checkPendingDialog, 500);
    return () => clearTimeout(timeoutId);
  }, [user, authLoading]);

  const handleSaveCredentials = async () => {
    console.log('[Index] handleSaveCredentials called');
    
    if (!pendingEmail || !pendingPassword) {
      console.error('[Index] Missing credentials');
      setShowSaveCredentialsDialog(false);
      return;
    }

    try {
      const success = await storeCredentials(pendingEmail, pendingPassword);
      console.log('[Index] Store credentials result:', success);
      
      if (success) {
        toast({
          title: "Başarılı",
          description: "Bilgileriniz güvenli bir şekilde kaydedildi. Bir sonraki girişte biyometrik kimlik doğrulama kullanabilirsiniz.",
        });
      } else {
        toast({
          title: "Hata",
          description: "Bilgiler kaydedilemedi. Lütfen tekrar deneyin.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('[Index] Error saving credentials:', error);
      toast({
        title: "Hata",
        description: "Bilgiler kaydedilemedi. Lütfen tekrar deneyin.",
        variant: "destructive"
      });
    } finally {
      setShowSaveCredentialsDialog(false);
      setPendingEmail(null);
      setPendingPassword(null);
    }
  };

  const handleDeclineSaveCredentials = () => {
    console.log('[Index] User declined to save credentials');
    setShowSaveCredentialsDialog(false);
    setPendingEmail(null);
    setPendingPassword(null);
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="glass-panel-strong rounded-2xl p-6 md:p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-h1 font-bold mb-2">
                Hoş Geldiniz, {username}
              </h1>
              <p className="text-sm sm:text-base md:text-body text-muted-foreground">
                {companyName} - Mağaza Yönetimi ve Müşteri Takip Sistemi
              </p>
            </div>
            <div className="hidden md:flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 border border-accent/20">
              <span className="text-2xl font-bold text-accent">
                {username.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions - Most Important Section */}
        <div className="glass-panel-strong rounded-2xl p-6 md:p-8 shadow-xl">
          <QuickActions />
        </div>

        {/* Today's Reminders Alert and Card */}
        <TodayReminders />

        {/* Dashboard Statistics */}
        <DashboardStatistics />

        {/* Stats Cards */}
        <StatsCards />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
            <WhosInside />
            <RecentActivity />
            {/* Last 5 Days Sales - Moved to less prominent position */}
            <Last5DaysSales />
          </div>

          {/* Right Column */}
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            {/* This column can be used for other widgets in the future */}
          </div>
        </div>
      </div>

      <FloatingQR />

      {/* Save Credentials Dialog */}
      <AlertDialog open={showSaveCredentialsDialog} onOpenChange={setShowSaveCredentialsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bilgilerinizi Kaydetmek İster misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              Bilgilerinizi güvenli bir şekilde kaydederek bir sonraki girişte biyometrik kimlik doğrulama (Face ID/Touch ID) kullanabilirsiniz. 
              Bilgileriniz cihazınızın güvenli depolama alanında saklanacaktır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeclineSaveCredentials}>
              Hayır
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveCredentials}>
              Evet, Kaydet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Update Required Modal */}
      {versionCheckResult && versionCheckResult.requirement && (
        <UpdateRequiredModal
          isOpen={showUpdateModal}
          title={versionCheckResult.requirement.message_title}
          description={versionCheckResult.requirement.message_description}
          appStoreUrl={
            versionCheckResult.platform === 'web'
              ? null
              : getAppStoreUrl(versionCheckResult.requirement, versionCheckResult.platform)
          }
          platform={versionCheckResult.platform}
          currentVersion={versionCheckResult.currentVersion}
          minimumVersion={versionCheckResult.requirement.minimum_version}
        />
      )}
    </Layout>
  );
};

export default Index;
